import { useEffect, useState } from 'react';
import { House } from 'lucide-react';
import { Typography } from '@/components/ui/typography';
import { AssembleLoader } from '@/components/ui/assemble-loader';
import { cn } from '@/lib/utils';
import {
  STATUS_TONE_CLASSES,
  statusToTone,
} from '../property-view/metadata/propertyConfig';
import {
  selectHeroUrl,
  selectThumbUrl,
  type HeroSource,
} from '../property-view/property-detail-drawer/types';

type Props = {
  source: HeroSource;
  /** Display value for the leasing-status pill (e.g., "Vacant"). Pass null/undefined to omit. */
  leasingStatus: string | null;
  ariaLabel: string;
};

export const HeroCarousel = ({ source, leasingStatus, ariaLabel }: Props) => {
  const { logoUrl, photoset, isPhotosetLoading } = source;
  // Filter to photo-type assets only — matches the manage-assets page's
  // canonical filter (usePropertyAssets.ts:135). Photosets can contain
  // non-photo entries (e.g. type:'url' Zillow 3D links).
  //
  // Then sort by sortOrder ASC. Backend's logoUrl picks the photo with
  // the lowest sortOrder (AssetsServiceImpl.getLogoUrl); manage-assets
  // sorts the same way (usePropertyAssets.ts:51). Without this sort,
  // the hero's assets[0] depends on whatever order the API happens to
  // return — non-deterministic and visibly mismatches the property
  // card's image (which is the backend-set logoUrl).
  const assets = (photoset?.assets ?? [])
    .filter((a) => a.type === 'photo' || a.type === null)
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset selection when the photoset arrives or the property changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [photoset?.photoset.photosetId]);

  // Hero state-table per spec §3 / plan-time corrected
  const photosetHero = assets[selectedIndex];
  const photosetUrl = photosetHero ? selectHeroUrl(photosetHero) : null;

  let heroContent: React.ReactNode;
  if (photosetUrl) {
    heroContent = (
      <img
        src={photosetUrl}
        alt={ariaLabel}
        className="h-full w-full object-cover transition-opacity duration-200"
      />
    );
  } else if (isPhotosetLoading) {
    heroContent = (
      <div className="flex h-full w-full items-center justify-center bg-background">
        <AssembleLoader size={88} />
      </div>
    );
  } else if (logoUrl) {
    heroContent = (
      <img src={logoUrl} alt={ariaLabel} className="h-full w-full object-cover" />
    );
  } else {
    // Terminal fallback: both logoUrl and photoset empty
    heroContent = (
      <div
        className="flex h-full w-full items-center justify-center"
        aria-label={ariaLabel}
        data-testid="hero-no-property-fallback"
      >
        <House
          className="h-24 w-24 text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
    );
  }

  const tone = statusToTone(leasingStatus);

  return (
    <div className="shrink-0">
      <div className="relative aspect-[3/2] bg-muted">
        {heroContent}
        {leasingStatus && (
          <span
            className={cn(
              'absolute top-3 left-3 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5',
              STATUS_TONE_CLASSES[tone],
            )}
          >
            <Typography
              as="span"
              variant="small-semibold"
              className="uppercase tracking-wider"
            >
              {leasingStatus}
            </Typography>
          </span>
        )}
      </div>
      {assets.length > 1 && (
        <div className="flex gap-2 px-6 py-3 overflow-x-auto">
          {assets.map((asset: typeof assets[0], i: number) => {
            const thumbUrl = selectThumbUrl(asset);
            if (!thumbUrl) return null;
            return (
              <button
                key={asset.assetUUID}
                type="button"
                onClick={() => setSelectedIndex(i)}
                aria-label={`Photo ${i + 1} of ${assets.length}`}
                className={cn(
                  'h-12 w-12 shrink-0 rounded-md overflow-hidden ring-2 ring-transparent focus:outline-none focus-visible:ring-primary/25',
                  i === selectedIndex && 'ring-primary',
                )}
              >
                <img
                  src={thumbUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
